import { Controller } from '@hotwired/stimulus';
import Swiper from 'swiper';
import 'swiper/css';
import 'swiper/css/effect-fade';
import { Mousewheel, Keyboard, FreeMode, Scrollbar, Pagination, EffectFade, Autoplay } from 'swiper/modules';
import { useMatchMedia } from 'stimulus-use';

import {
  handleSwiperBullets,
  autoPlayValueToPercentage,
  handleSwiperBulletsNoAutoplay,
} from '../utilities/swiperHelpers.js';

export default class extends Controller {
  static targets = ['swiper', 'wrapper', 'slide', 'scrollbar', 'navigation', 'pagination'];
  static values = {
    params: Object,
  };

  initialize() {}

  connect() {
    useMatchMedia(this, {
      mediaQueries: {
        mobile: '(max-width: 768px)',
      },
    });

    const mobileMediaQuery = window.matchMedia('(max-width: 768px)');
    if (this.paramsValue.preset != 'topicsSlider') {
      this.initStorySlider();
    }
  }

  isMobile({ name, media, matches, event }) {
    if (matches && this.paramsValue.preset == 'topicsSlider') {
      this.destroySlider();
      this.initDefaultSlider();
    }
  }

  notMobile({ name, media, matches, event }) {
    if (!matches && this.paramsValue.preset == 'topicsSlider') {
      this.destroySlider();
      this.initStorySlider();
    }
  }

  initStorySlider() {
    let prevPercentage;
    const options = {
      modules: [Mousewheel, Keyboard, EffectFade, Autoplay, Pagination],
      direction: 'horizontal',
      slidesPerView: 1,
      spaceBetween: 0,
      keyboard: {
        enabled: true,
      },
      mousewheel: {
        releaseOnEdges: true,
        forceToAxis: true,
      },
      autoplay: {
        delay: 5000,
        disableOnInteraction: true,
      },
      effect: 'fade',
      rewind: true,
      pagination: {
        clickable: true,
        el: this.paginationTarget,
        type: 'bullets',
        renderBullet: function (index, className) {
          return `<span class="${className} relative flex-1 cursor-pointer h-5 flex items-center">
            <span class="absolute -z-10 left-0 top-1/2 -translate-y-1/2 w-full block h-0.75 bg-darkgreen-300 rounded-full"></span>
            <span class="bullet-progress w-0 block h-0.75 bg-white rounded-full"></span>
          </span>`;
        },
      },
      on: {
        transitionEnd: function (swiper) {
          if (swiper.autoplay.running) {
            const currentSlide = swiper.slides[swiper.activeIndex];
            handleSwiperBullets(swiper);
          } else {
            handleSwiperBulletsNoAutoplay(swiper);
          }
        }.bind(this),
        autoplayTimeLeft: function (swiper, timeLeft, percentage) {
          const activeBullet = swiper.pagination.bullets[swiper.activeIndex];
          if (activeBullet) {
            const activeProgressBar = activeBullet.querySelector('.bullet-progress');
            const widthValue = autoPlayValueToPercentage(percentage);
            if (percentage === prevPercentage) {
              activeProgressBar.style.width = '0%';
            } else {
              activeProgressBar.style.width = `${widthValue}%`;
            }
            prevPercentage = percentage;
          }
        }.bind(this),
      },
    };
    this.initSlider(options);
  }

  initDefaultSlider() {
    const options = {
      modules: [Mousewheel, Keyboard],
      direction: 'horizontal',
      slidesPerView: 'auto',
      spaceBetween: this.paramsValue.spaceBetween,
      grabCursor: true,
      keyboard: {
        enabled: true,
      },
      mousewheel: {
        releaseOnEdges: true,
        forceToAxis: true,
      },
    };
    this.initSlider(options);
  }

  initSlider(options) {
    this.swiperTarget.classList.add('swiper');
    this.wrapperTarget.classList.add('swiper-wrapper');
    this.slideTargets.forEach((item) => {
      item.classList.add('swiper-slide');
    });
    this.swiper = new Swiper(this.swiperTarget, options);
  }

  destroySlider() {
    this.swiper?.destroy(true, true);
    this.swiperTarget.classList.remove('swiper');
    this.swiperTarget.classList.remove('swiper-backface-hidden');
    this.wrapperTarget.classList.remove('swiper-wrapper');
    this.slideTargets.forEach((item) => {
      item.classList.remove('swiper-slide');
    });
  }
}
